import { useState, useEffect, useContext } from "react";
import { useRecoilState } from "recoil";
import { currentSlideAtom } from "../../atom";
import { ViewerContext } from "../../page/Viewer";

import styled from "styled-components";
import {
  Modal,
  Input,
  Button,
  Flex,
  Form,
  Select,
} from "antd";
import { DeleteOutlined, LoadingOutlined, PlusCircleOutlined, EditOutlined } from "@ant-design/icons";

import {
  deleteComment,
  getSlideData,
  postComment,
  putComment,
  patchSlideData,
  downloadSlide,
} from "../../api/slideAPI";
import { checkUser } from "../../api/authAPI";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import FileSaver from "file-saver";

const SideMenu = () => {
  const { userCustomSet } = useContext(ViewerContext);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);

  // 어노테이션 정보 훅 사용
  const annotationInfo = useAnnotationInfo({ currentSlide, setCurrentSlide });

  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCommentModal, setShowCommentModal] = useState<boolean>(false);
  const [commentContent, setCommentContent] = useState<string>("");
  const [currentCommentId, setCurrentCommentId] = useState<number>(0);
  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [form] = Form.useForm<any>();

  const editSlideInformation = async (values: any) => {
    values.slideId = currentSlide.slideId;
    await patchSlideData(values);

    // 수정 후 데이터 새로고침
    const data = await getSlideData(currentSlide.slideId);

    // 어노테이션이 비활성화 상태라면 어노테이션 데이터를 제거
    if (!userCustomSet?.useAnnotation) {
      data.annotations = [];
    }

    setCurrentSlide(data);

    setShowEditModal(false);
  };

  const cancelEdit = async () => {
    form.resetFields();
    setShowEditModal(false);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    const fileData = await downloadSlide(currentSlide.slideId);
    if(fileData === false) {
      setIsDownloading(false);
      window.alert("다운로드 권한이 없습니다.");
      return;
    }
    FileSaver.saveAs(fileData, currentSlide.filename);
    setIsDownloading(false);
  };

  // 어노테이션 편집 핸들러 (사이드바용)
  const handleEditAnnotationFromSidebar = (annotationId: number) => {
    // currentSlide에서 최신 어노테이션 데이터를 찾음
    const latestAnnotation = currentSlide?.annotations?.find(
      (anno: any) => anno.annoId === annotationId
    );

    if (latestAnnotation) {
      annotationInfo.setSelectedAnnotation(latestAnnotation);

      // 상태 업데이트 후 모달 열기
      setTimeout(() => {
        annotationInfo.setShowEditModal(true);
      }, 0);
    }
  };

  const addComment = async () => {
    if (commentContent !== "") {
      currentCommentId === 0
        ? await postComment(currentSlide.slideId, commentContent)
        : await putComment(currentCommentId, commentContent);

      // 추가/수정 후 데이터 새로고침
      const data = await getSlideData(currentSlide.slideId);
      setCurrentSlide(data);
    }
    setShowCommentModal(false);
    setCommentContent("");
  };

  const clickDeleteComment = async (commentId: number) => {
    const isAdmin = await checkUser();
    if (isAdmin !== "ADMIN_MAIN") {
      window.alert("삭제 권한이 없습니다.");
      return;
    }
    if (window.confirm(`해당 코멘트를 삭제하시겠습니까?`)) {
      await deleteComment(commentId);
      getSlideData(currentSlide.slideId).then((data) => setCurrentSlide(data));
    }
  };

  useEffect(() => {
    checkUser().then((role) => {
      setShowDownloadBtn(role === "ADMIN_MAIN");
    });
  }, []);


  // 2) add components
  const DisplayColor = (props: any) => {
    const { annotation } = props;
    const annoInfo = annotation?.annoInfo;

    // 화살표(g 그룹)인 경우와 일반 어노테이션 구분
    let color;
    if (annoInfo && annoInfo[0] === 'g' && annoInfo[1]?.class === 'annotation arrow-group') {
      color = annoInfo[1]?.stroke || '#285F3D';
    } else {
      color = annoInfo?.[1]?.stroke;
    }

    const style = {
      width: "1rem",
      height: "1rem",
      backgroundColor: color,
      borderRadius: "3px",
      marginLeft: "3px",
    };

    return (
      <>
        <div style={style}></div>
      </>
    );
  };

  useEffect(() => {
    console.log({ currentSlide });
  }, [currentSlide]);

  return (
    <StMainWrapper>
      <StSectionTitle>
        INFORMATION
        <Button
          type="link"
          style={{ float: "right" }}
          onClick={() => setShowEditModal(true)}
        >
          Edit
        </Button>
      </StSectionTitle>
      <StSectionContent
        style={{
          display: "grid",
          gridTemplateColumns: "8rem auto",
          rowGap: "10px",
        }}
      >
        <StInfoLabel>Slide Name</StInfoLabel>
        <StInfoValue>{currentSlide ? currentSlide.slidename : "-"}</StInfoValue>
        <StInfoLabel>Caption</StInfoLabel>
        <StInfoValue>{currentSlide ? currentSlide.caption : "-"}</StInfoValue>
        <StInfoLabel>Gender</StInfoLabel>
        <StInfoValue>{currentSlide ? currentSlide.gender : "-"}</StInfoValue>
        <StInfoLabel>Age</StInfoLabel>
        <StInfoValue>{currentSlide ? currentSlide.age : "-"}</StInfoValue>
        <StInfoLabel>Site</StInfoLabel>
        <StInfoValue>{currentSlide ? currentSlide.site : "-"}</StInfoValue>
        <StInfoLabel>Diagnosis</StInfoLabel>
        <StInfoValue>
          {currentSlide ? currentSlide.zindanName : "-"}
        </StInfoValue>
        <div></div>
        {showDownloadBtn && 
          <div>
            <button onClick={handleDownload} style={{ float: 'right' }}>file download</button>
          </div>
        }
      </StSectionContent>
      <StSectionTitle>ANNOTATION</StSectionTitle>
      <StSectionContent>
        {(currentSlide?.annotations || []).map((annotation, idx) => (
          <StAnnotationBox key={`${idx}_${annotation.annoId}`}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "0.8rem",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.3rem" }}>
                  <DisplayColor annotation={annotation} />
                  <div 
                  title={`${annotation.content}`}
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth:"10rem"
                  }}
                  >
                    {annotation.content}
                  </div>
                  <div style={{
                    fontSize: "0.75rem",
                    color: "#aaa",
                    padding: "2px 6px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "3px",
                    flexShrink: 0
                  }}>
                    {annotationInfo.getShapeType(annotation)}
                  </div>
                </div>
                <div style={{
                  fontSize: "0.85rem",
                  color: "#888",
                  marginLeft: "1.5rem"
                }}>
                  {annotation.writer}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                <EditOutlined
                  onClick={() => handleEditAnnotationFromSidebar(annotation.annoId)}
                  style={{
                    fontSize: "1.1rem",
                    cursor: "pointer",
                    color: "#888",
                  }}
                />
                <DeleteOutlined
                  onClick={() => {
                    annotationInfo.setSelectedAnnotation(annotation);
                    annotationInfo.handleDeleteAnnotation();
                  }}
                  style={{
                    fontSize: "1.1rem",
                    cursor: "pointer",
                    color: "#888",
                  }}
                />
              </div>
            </div>
          </StAnnotationBox>
        ))}
      </StSectionContent>
      <StSectionTitle>
        COMMENT
        <PlusCircleOutlined
          onClick={() => {
            setCurrentCommentId(0);
            setShowCommentModal(true);
          }}
          style={{
            position: "absolute",
            cursor: "pointer",
            fontSize: "1.4rem",
            right: "20px",
          }}
        />
      </StSectionTitle>
      <StSectionContent>
        {(currentSlide?.slideComments || []).map((comment) => (
          <StCommentBox key={comment.slideCommentId}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "0.5rem"
            }}>
              <div style={{
                fontSize: "0.95rem",
                color: "#222",
                lineHeight: "1.5",
                wordBreak: "break-word",
                flex: 1,
                marginBottom: "0.4rem"
              }}>
                {comment.content}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                <EditOutlined
                  onClick={() => {
                    setCurrentCommentId(comment.slideCommentId);
                    setShowCommentModal(true);
                  }}
                  style={{
                    fontSize: "1rem",
                    cursor: "pointer",
                    color: "#999",
                  }}
                />
                <DeleteOutlined
                  onClick={() => clickDeleteComment(comment.slideCommentId)}
                  style={{
                    fontSize: "1rem",
                    cursor: "pointer",
                    color: "#999",
                  }}
                />
              </div>
            </div>
            <div style={{
              fontSize: "0.75rem",
              color: "#999"
            }}>
              {comment.writer}
            </div>
          </StCommentBox>
        ))}
      </StSectionContent>
      <Modal
        centered
        title="Edit Information"
        open={showEditModal}
        onCancel={cancelEdit}
        footer={null}
      >
        <Flex gap="middle" vertical>
          <Form
            form={form}
            onFinish={editSlideInformation}
            initialValues={currentSlide}
            layout="vertical"
          >
            <Form.Item
              name="slidename"
              label="Slide Name"
              // rules={[
              //   { required: true, message: "Please input the slide name" },
              // ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="caption"
              label="Caption"
              // rules={[{ required: true, message: "Please input the caption" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="gender"
              label="Gender"
              // rules={[{ required: true, message: "Please select the gender" }]}
            >
              <Select>
                <Select.Option value="M">Male</Select.Option>
                <Select.Option value="F">Female</Select.Option>
                <Select.Option value="U">Unknown</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="age"
              label="Age"
              // rules={[{ required: true, message: "Please input the age" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="site"
              label="Site"
              // rules={[{ required: true, message: "Please input the site" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="zindanName"
              label="Diagnosis"
              rules={[
                // { required: true, message: "Please input the diagnosis" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={cancelEdit} style={{ marginRight: "10px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>
      <Modal
        centered
        title="Add Comment"
        open={showCommentModal}
        onOk={addComment}
        onCancel={() => setShowCommentModal(false)}
      >
        <Input onChange={(e) => setCommentContent(e.target.value)} />
      </Modal>

      {/* 어노테이션 수정 모달 */}
      <AnnotationInfoPanelComponent {...annotationInfo} />

      {isDownloading &&
        <Modal open={isDownloading} centered closable={false} footer={null}>
          Downloading...  <LoadingOutlined />
        </Modal>
      }
    </StMainWrapper>
  );
};

const StMainWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StSectionTitle = styled.div`
  position: relative;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  padding: 15px 0;
  border-bottom: 1px solid lightgray;
  flex-shrink: 0;
`;

const StSectionContent = styled.div`
  padding: 10px;
  border-bottom: 1px solid gray;
  overflow-y: auto;
  min-height: 0;

  /* 스크롤바 스타일 개선 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StAnnotationBox = styled.div`
  padding: 0.4rem;
  border: 1px solid lightgray;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StCommentBox = styled.div`
  padding: 0.8rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 0.6rem;
  border-left: 3px solid #ddd;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StInfoLabel = styled.div`
  border-right: 1px solid gray;
  font-size: 1rem;
  font-weight: 600;
`;

const StInfoValue = styled.div`
  margin-left: 1rem;
`;

export default SideMenu;
